import {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import clsx from "clsx";
import styles from "../chatDrawer.module.css";
import useChatSessionStore from "../../../../store/chatSessionStore";
import useChatMessageStore from "../../../../store/chatMessagesStore";

interface HistoryItem {
  id: string;
  project_id: string;
  title: string;
  description: string;
  timestamp: string;
  selected?: boolean;

}

export interface ProjectSidebarConfig {
  title?: string;
  logoAnimationUrl?: string;
  logoOverlayUrl?: string;
  logoAnimationAlt?: string;
  logoOverlayAlt?: string;
  logoVideoUrl?: string;
  logoVideoAlt?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
}

export interface ProjectSidebarProps extends ProjectSidebarConfig {
  isOpen: boolean;
  isSearchFieldNeeded?: boolean;
  onClose: () => void;
  initialItems?: HistoryItem[];
  aiAssistantName?: string;
  onSelectSession?: (
    indexId: number,
    projectId: string,
    totalCount: number

  ) => void;
}

const ProjectSidebar = forwardRef(function ProjectSidebar(
  {
    // isOpen,
    // onClose,
    // initialItems = [],
    onSelectSession,
    isSearchFieldNeeded = true,
    aiAssistantName = "AI",
    title = "My AI",
    logoAnimationUrl,
    logoOverlayUrl,
    logoAnimationAlt = "aufgangAi",
    logoOverlayAlt = "aufgangAi",
    logoVideoUrl,
    // logoVideoAlt = "aufgangAi",
    searchPlaceholder = "Search in Projects",
    noResultsText = "No history found.",
  }: ProjectSidebarProps,
  ref
) {
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [filterdHistoryItems, setFilterdHistoryItems] = useState<HistoryItem[]>([]);
  const [_isSearching, setIsSearching] = useState(false); // Add this state
  const { setIsWebEnabled } = useChatMessageStore();

  const {
    chatSessions,
    fetchChatSessions,
    // isLoading,
    selectedSessionId,
    setSelectedSessionId,
    setSessionIsOn,
    // dropdownSelectedProjectId,
    setDropdownSelectedProjectId,
    historySearch,
    setHistorySearch,
  } = useChatSessionStore();

  // Update historyItems when API returns data
  useEffect(() => {
    if (chatSessions && chatSessions && Array.isArray(chatSessions) && !_isSearching) {
      setHistoryItems(
        chatSessions.map((session) => ({
          id: session._id,
          project_id: session.project_id || session.project_name || session._id,
          title: (session.session_title === 'default' || session.project_name === 'default') ? aiAssistantName : session.session_title || session.project_name,
          description: session.last_message || "No messages yet.",
          timestamp: session.timestamp
            ? new Date(session.timestamp).toLocaleString()
            : "",
        }))
      );


      setFilterdHistoryItems(
        chatSessions.map((session) => ({
          id: session._id,
          project_id: session.project_id || session.project_name || session._id,
          // title: session.session_title || session.project_name,
          title: (session.session_title === 'default' || session.project_name === 'default') ? aiAssistantName : session.session_title || session.project_name,
          description: session.last_message || "No messages yet.",
          timestamp: session.timestamp
            ? new Date(session.timestamp).toLocaleString()
            : "",
        }))
      );
    }
  }, [chatSessions]);

  // Clear selection on open or data change
  // useEffect(() => {
  //   setSelectedSessionId(null); // clear selection on open or data change
  // }, [isOpen, chatSessions]);

  // useEffect(() => {
  //   if (isOpen && historyItems.length > 0) {
  //     setSelectedSessionId(historyItems[0].project_id); // Select first item by default
  //   } else {
  //     setSelectedSessionId(null);
  //   }
  // }, [isOpen, historyItems]);


  useEffect(() => {
    return () => {
      setHistorySearch('')
    };
  }, []);


  const selectItem = (id: string, project_id: string) => {
    setSelectedSessionId(project_id); // <-- set selected
    setDropdownSelectedProjectId(project_id); // <-- keep dropdown in sync
    setSessionIsOn(true);
    setIsWebEnabled(false); // Reset to work mode when changing project
    const selected =
      chatSessions && Array.isArray(chatSessions)
        ? chatSessions.find((session: any) => session._id === id)
        : undefined;
    if (selected && selected.index_id && onSelectSession) {
      onSelectSession(
        selected.index_id,
        selected.project_name,
        selected.total_sessions_count
      );
    }
  };

  useImperativeHandle(ref, () => ({
    fetchChatSessions,
  }));

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleInputChange = (value: string) => {
    setHistorySearch(value);

    // Only set searching state if there's actual search text
    if (value.trim()) {
      setIsSearching(true);
      const filtered = historyItems.filter(item =>
        item.title.toLowerCase().includes(value.trim().toLowerCase())
      );

      setFilterdHistoryItems(filtered);
    } else {
      setIsSearching(false);
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchChatSessions(value.trim() || "");
    }, 300); // 300ms debounce
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // if (!isOpen) return null;

  const formatTimestamp = (timestamp: string): string => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.warn('Invalid timestamp:', timestamp);
    return timestamp; // Return original string as fallback
  }
  
  // Manually format to ensure consistency across all locales
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  
  return `${month}/${day} ${hours}:${minutes} ${ampm}`;
};

  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center">
          <div
            className={clsx(
              "relative w-12 h-12",
              logoVideoUrl
                ? "sm:w-12 sm:h-12 rounded-full overflow-hidden"
                : "sm:w-auto sm:h-auto"
            )}
          >
            {logoVideoUrl ? (
              <img src={logoVideoUrl} alt="Animated GIF" />
            ) : (
              <>
                <img
                  src={logoAnimationUrl || "/placeholder.svg"}
                  alt={logoAnimationAlt}
                  className={`${["rotate-animation"]} cursor-pointer`}
                />
                <img
                  src={logoOverlayUrl || "/placeholder.svg"}
                  alt={logoOverlayAlt}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                />
              </>
            )}
          </div>
          <span className="font-semibold text-[18px] sm:text-lg ms-2 truncate">{title}</span>
        </div>
        {/* <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close modal"
        >
          <img src={chat_close_icon} alt="Close" />
        </button> */}
      </div>
      {isSearchFieldNeeded && <div className="p-4 pb-2 me-1">
        <input
          type="text"
          className="w-full p-2 border border-inactive-bg rounded-md text-gray-900 font-normal text-sm placeholder-gray-400 focus:outline-none focus:border-inactive-bg"
          placeholder={searchPlaceholder}
          value={historySearch}
          onChange={(e) => handleInputChange(e.target.value)}
        />
      </div>}

      {/* Content */}
      <div className="overflow-y-auto max-h-[76vh] p-3 me-1">
        {/* {isLoading && (
          <div>
            {[...Array(8)].map((_, idx) => (
              <div
                key={idx}
                className="animate-pulse p-4 border-b border-gray-100 rounded-lg mb-3 bg-gray-100"
              >
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3 mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
            ))}
          </div>
        )} */}
        {filterdHistoryItems.length === 0 && (
          <div className="text-center text-xs text-gray-500 py-4">
            {chatSessions &&
              !Array.isArray(chatSessions) &&
              typeof chatSessions === "object" &&
              "message" in chatSessions
              ? (chatSessions as { message: string }).message
              : noResultsText}
          </div>
        )}
        {filterdHistoryItems.map((item) => (
          <div
            key={item.id}
            className={`p-3 border-b border-gray-100 rounded-lg cursor-pointer mb-1 ${selectedSessionId === item.project_id
              ? "bg-chat-dropdown-bg"
              : "hover:bg-gray-50"
              }`}
            onClick={() => selectItem(item.id, item.project_id || item.id)}
          >
            <div className="flex justify-between items-start mb-1">
              <h3 className="text-[16px] sm:text-sm font-medium text-gray-900 mt-0">
                {item.title.toLowerCase() === aiAssistantName
                  ? aiAssistantName
                  : item.title.charAt(0).toUpperCase() + item.title.slice(1)}
              </h3>
              <span className="text-xs text-gray-500">
                {formatTimestamp(item.timestamp)}
              </span>
            </div>
            <p className={`text-xs sm:text-sm text-gray-600 ${styles.chatHistoryItem}`}>{item.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
});

export default ProjectSidebar;
